import React from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import styles from './ProgressBar.module.scss';

export type ProgressBarProps = {
  value: number;
  min?: number;
  max?: number;
  type?: 'default' | 'success' | 'error';
  label?: string;
  showCaption?: boolean;
};

const boundedValue = ({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}) => {
  if (!Number.isFinite(value)) return min;
  if (max <= min) return min;

  return Math.min(Math.max(value, min), max);
};

const progressPercentage = ({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}) => {
  if (max <= min) return 0;

  return ((value - min) / (max - min)) * 100;
};

export const ProgressBar = ({
  value,
  showCaption = false,
  type = 'default',
  label = 'Progress',
  min = 0,
  max = 100,
}: ProgressBarProps) => {
  const boundedProgressValue = boundedValue({ value, min, max });
  const progressWidth = `${progressPercentage({
    value: boundedProgressValue,
    min,
    max,
  })}%`;

  return (
    <div
      className={styles.ProgressBarContainer}
      role="progressbar"
      aria-valuenow={boundedProgressValue}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={label}
    >
      <div className={styles.ProgressBarFrame}>
        <motion.div
          className={classNames(styles.ProgressBarFilling, styles[type])}
          animate={{ width: progressWidth }}
          initial={false}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        />
      </div>

      {showCaption && (
        <span className={styles.ProgressBarCaption}>
          {min !== 0
            ? `${boundedProgressValue}/${min}..${max}`
            : `${boundedProgressValue}/${max}`}
        </span>
      )}
    </div>
  );
};
